/* Reis-Nichols Spring Catalog — accessible components.
   Accordion: WAI-ARIA disclosure buttons (aria-expanded / aria-controls).
   Tabs: WAI-ARIA tabs (roving tabindex, Arrow / Home / End keys).
   Carousel: labelled prev/next + dot buttons, keyboard-scrollable, no autoplay. */
(function () {
  'use strict';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function reflow() { window.dispatchEvent(new CustomEvent('rn:reflow')); }

  /* ---------- Accordion ---------- */
  function initAccordion(root) {
    if (root.dataset.rnReady) return; root.dataset.rnReady = '1';
    var single = root.hasAttribute('data-single');
    var triggers = Array.prototype.slice.call(root.querySelectorAll('.rn-acc__trigger'));
    function set(btn, open) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
      if (!open && panel) {
        panel.querySelectorAll('video').forEach(function (v) { if (!v.paused) v.pause(); });
      }
    }
    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        if (open && single) triggers.forEach(function (o) { if (o !== btn) set(o, false); });
        set(btn, open);
        reflow();
      });
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    if (root.dataset.rnReady) return; root.dataset.rnReady = '1';
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var p = document.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
      reflow();
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') n = tabs[0];
        else if (e.key === 'End') n = tabs[tabs.length - 1];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });
  }

  /* ---------- Carousel ---------- */
  function initCarousel(root) {
    if (root.dataset.rnReady) return; root.dataset.rnReady = '1';
    var vp = root.querySelector('.rn-carousel__viewport');
    var track = root.querySelector('.rn-carousel__track');
    var prev = root.querySelector('.rn-carousel__btn--prev');
    var next = root.querySelector('.rn-carousel__btn--next');
    var dotsWrap = root.querySelector('.rn-carousel__dots');
    var live = root.querySelector('.rn-carousel__status');
    var slides = Array.prototype.slice.call(track.children);
    var total = slides.length, maxIndex = 0, current = 0, dots = [], raf = 0;

    // A scroll container with no focusable content must itself be keyboard-reachable.
    if (!vp.querySelector('a,button,input,select,textarea,video,[tabindex]')) {
      vp.tabIndex = 0;
      vp.setAttribute('role', 'group');
      vp.setAttribute('aria-label', vp.getAttribute('data-label') || 'Slides');
      vp.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(current + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(current - 1, true); }
      });
    }

    function step() {
      if (slides.length < 2) return slides[0] ? slides[0].offsetWidth : 1;
      return slides[1].offsetLeft - slides[0].offsetLeft;
    }
    function measure() {
      if (!vp.clientWidth) return; // inside a hidden panel; re-run on rn:reflow
      var s = step() || 1;
      maxIndex = Math.max(0, Math.ceil((vp.scrollWidth - vp.clientWidth) / s - 0.05));
      var hasNav = maxIndex > 0;
      if (prev) prev.hidden = !hasNav;
      if (next) next.hidden = !hasNav;
      if (dotsWrap) {
        if (dotsWrap.dataset.enabled !== 'true' || !hasNav) { dotsWrap.hidden = true; }
        else { if (dots.length !== maxIndex + 1) buildDots(); dotsWrap.hidden = false; }
      }
      update();
    }
    function buildDots() {
      dotsWrap.innerHTML = ''; dots = [];
      for (var i = 0; i <= maxIndex; i++) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'rn-carousel__dot';
        b.setAttribute('aria-label', 'Show slide ' + (i + 1) + ' of ' + total);
        b.appendChild(document.createElement('span'));
        (function (idx) { b.addEventListener('click', function () { go(idx, true); }); })(i);
        dotsWrap.appendChild(b); dots.push(b);
      }
      dotsWrap.hidden = false;
    }
    function update() {
      var s = step() || 1;
      current = Math.min(maxIndex, Math.max(0, Math.round(vp.scrollLeft / s)));
      if (prev) prev.setAttribute('aria-disabled', current <= 0 ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', current >= maxIndex ? 'true' : 'false');
      dots.forEach(function (d, i) { if (i === current) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
    }
    function go(i, announce) {
      i = Math.min(maxIndex, Math.max(0, i));
      vp.scrollTo({ left: i * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      if (announce && live) live.textContent = 'Showing slide ' + (i + 1) + ' of ' + total;
    }
    if (prev) prev.addEventListener('click', function () { if (current > 0) go(current - 1, true); });
    if (next) next.addEventListener('click', function () { if (current < maxIndex) go(current + 1, true); });
    vp.addEventListener('scroll', function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('rn:reflow', function () { requestAnimationFrame(measure); });
    if ('ResizeObserver' in window) new ResizeObserver(function () { measure(); }).observe(vp);
    else window.addEventListener('resize', measure);
    measure();
  }

  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-rn-accordion]').forEach(initAccordion);
    scope.querySelectorAll('[data-rn-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-rn-carousel]').forEach(initCarousel);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
